// src/pages/TabControlTestPage.tsx
// Test page for the TabControl component

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useSocketConnectionId } from '../contexts/SocketContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Settings, Plus, Pin, Edit3 } from 'lucide-react';
import logoCorge from '../assets/logos/logo-corgee.png';

import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import TabControl from '../components/TabControl/TabControl';
import type { TabControlConfig, ReactFunctions, Dashboard } from '../components/TabControl/types';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';

export const TabControlTestPage: React.FC = () =>
{
  const toast = useToast();
  const socketConnectionId = useSocketConnectionId();

  // State for dashboard information
  const [allDashboards, setAllDashboards] = useState<Dashboard[]>([]);
  const [pinnedDashboards, setPinnedDashboards] = useState<Dashboard[]>([]);
  const [notPinnedDashboards, setNotPinnedDashboards] = useState<Dashboard[]>([]);
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(null);
  const reactFunctionsRef = useRef<ReactFunctions | null>(null);

  // State for configuration options
  const [configValueId, setConfigValueId] = useState<number>(1);
  const [projectId, setProjectId] = useState<number | undefined>(undefined);
  const [isOrganize, setIsOrganize] = useState<boolean>(false);
  const [isBookmarks, setIsBookmarks] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(true);

  // Get dynamic API configuration
  const getApiConfig = useCallback(() =>
  {
    const apiUrl = CorgeeStorageHelper.apiURL() || 'https://app.corgee.com';
    const token = CorgeeStorageHelper.getToken() || '';
    const tenancyName = CorgeeStorageHelper.appPrefix() || '';

    return {
      url: apiUrl,
      token: token,
      TenancyName: tenancyName,
    };
  }, []);

  // TabControl functions - memoized to prevent infinite loops
  const tabControlFunctions = useMemo(
    () => ({
      setDashboards: (
        dashboards: Dashboard[],
        pinned: Dashboard[],
        notPinned: Dashboard[],
        current: Dashboard | undefined,
        _isInitial?: boolean,
      ) =>
      {
        setAllDashboards(dashboards);
        setPinnedDashboards(pinned);
        setNotPinnedDashboards(notPinned);
        if (current)
        {
          setCurrentDashboard(current);
        }
      },
      setDashboard: (dashboard: Dashboard) =>
      {
        setCurrentDashboard(dashboard);
      },
      addDashboard: (_event: React.MouseEvent) => {},
      updateDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      pinDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      deleteDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      shareDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      duplicateDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      editDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      openEditDashboard: (_event: React.MouseEvent, _dashboard: Dashboard, _flag?: boolean) => {},
      archiveDashboardGroup: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      showReports: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      pinTabDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      OpenTabDashboard: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      attachReactFunctions: (functions: ReactFunctions) =>
      {
        // Use ref instead of state to avoid re-render loop
        reactFunctionsRef.current = functions;
      },
      loadingBarStart: () => {},
      loadingBarComplete: () => {},
      getSocketConnectionId: () => socketConnectionId,
      editSPPeople: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
      editCapacityTracker: (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    }),
    [socketConnectionId],
  );

  // Build config object - memoized to prevent unnecessary re-renders
  const tabControlConfig: TabControlConfig = useMemo(
    () => ({
      api: getApiConfig(),
      configValueId: configValueId,
      projectId: projectId,
      isOrganize: isOrganize,
      isBookmarks: isBookmarks,
      canEdit: canEdit,
      isAdmin: isAdmin,
      functions: tabControlFunctions,
    }),
    [
      getApiConfig,
      configValueId,
      projectId,
      isOrganize,
      isBookmarks,
      canEdit,
      isAdmin,
      tabControlFunctions,
    ],
  );

  // Handler for reload button
  const handleReload = () =>
  {
    if (reactFunctionsRef.current?.reloadDashboard)
    {
      reactFunctionsRef.current.reloadDashboard();
      toast.info('Reloading dashboards...', 'Reload');
    }
    else
    {
      toast.warning('React functions not available yet', 'Warning');
    }
  };

  // Handler for force arrows button
  const handleForceArrows = () =>
  {
    if (reactFunctionsRef.current?.ForceShowArrows)
    {
      reactFunctionsRef.current.ForceShowArrows();
      toast.info('Force show arrows called', 'Arrows');
    }
  };

  // Handler for open details
  const handleOpenDetails = () =>
  {
    if (reactFunctionsRef.current?.openDetails)
    {
      reactFunctionsRef.current.openDetails();
      toast.info('Open details called', 'Details');
    }
  };

  return (
    <div className="min-h-screen bg-[#ededed]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoCorge} alt="Corgee Logo" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-2xl font-semibold text-[#5dade2]">TabControl Test</h1>
              <p className="text-sm text-gray-500">Dashboard Tab Navigation Component</p>
            </div>
          </div>

          <Link to="/dashboard">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* TabControl Section */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
              1
            </span>
            TabControl Component
          </h2>

          <p className="text-gray-600 mb-4">
            Dashboard tab navigation with drag-and-drop reordering, pin/unpin functionality, and
            context menus.
          </p>

          {/* TabControl rendered */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <TabControl config={tabControlConfig} />
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={handleReload}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload Dashboards
            </Button>
            <Button variant="outline" onClick={handleForceArrows}>
              Force Show Arrows
            </Button>
            <Button variant="outline" onClick={handleOpenDetails}>
              Open Details Tab
            </Button>
          </div>
        </section>

        {/* Current Dashboard Info */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">
              2
            </span>
            Current Dashboard State
          </h2>

          {currentDashboard ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800">Selected Dashboard</h4>
                <p className="text-lg font-semibold text-blue-900">{currentDashboard.Name}</p>
                <p className="text-xs text-blue-600">ID: {currentDashboard.DashboardId}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="text-sm font-medium text-green-800">Pinned Dashboards</h4>
                <p className="text-lg font-semibold text-green-900">{pinnedDashboards.length}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <h4 className="text-sm font-medium text-amber-800">Not Pinned Dashboards</h4>
                <p className="text-lg font-semibold text-amber-900">{notPinnedDashboards.length}</p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gray-100 rounded-lg text-gray-500 text-center">
              No dashboard selected yet. Wait for initialization...
            </div>
          )}

          {/* Dashboard list */}
          {allDashboards.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-700 mb-3">
                All Dashboards ({allDashboards.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {allDashboards.map(dashboard => (
                  <div
                    key={dashboard.DashboardId}
                    className={`p-3 rounded-lg border ${
                      currentDashboard?.DashboardId === dashboard.DashboardId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-800">{dashboard.Name}</span>
                      <div className="flex items-center gap-1">
                        {dashboard.IsTabPinedDashboard && <Pin className="w-4 h-4 text-blue-500" />}
                        {dashboard.IsDefault && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Order: {dashboard.Order} | Type: {dashboard.FakeDashboardType ?? 'Standard'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Configuration Panel */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">
              3
            </span>
            Configuration Options
          </h2>

          <p className="text-gray-600 mb-4">
            Modify these options and refresh the page to test different configurations.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Config Value ID */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Config Value ID
              </label>
              <input
                type="number"
                value={configValueId}
                onChange={e => setConfigValueId(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                The configuration value ID for dashboards
              </p>
            </div>

            {/* Project ID */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project ID (optional)
              </label>
              <input
                type="number"
                value={projectId || ''}
                onChange={e => setProjectId(e.target.value ? Number(e.target.value) : undefined)}
                placeholder="Leave empty for none"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Optional project ID filter</p>
            </div>

            {/* Boolean options */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Boolean Options
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isOrganize}
                    onChange={e => setIsOrganize(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  isOrganize
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isBookmarks}
                    onChange={e => setIsBookmarks(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  isBookmarks
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={canEdit}
                    onChange={e => setCanEdit(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  canEdit
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isAdmin}
                    onChange={e => setIsAdmin(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  isAdmin
                </label>
              </div>
            </div>
          </div>

          {/* Current API Config */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Current API Configuration:</h4>
            <div className="text-sm text-blue-800 space-y-1 font-mono">
              <p>URL: {getApiConfig().url}</p>
              <p>
                Token:{' '}
                {getApiConfig().token ? `${getApiConfig().token.substring(0, 20)}...` : 'Not set'}
              </p>
              <p>Tenancy: {getApiConfig().TenancyName || 'Not set'}</p>
            </div>
          </div>
        </section>

        {/* Features Info */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center text-sm font-bold">
              4
            </span>
            TabControl Features
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-600" />
                Core Features
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Tab navigation with active state</li>
                <li>✓ Drag-and-drop reordering (dnd-kit)</li>
                <li>✓ Scroll arrows for overflow tabs</li>
                <li>✓ Double-click to rename tabs</li>
                <li>✓ Context menu actions</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Pin className="w-5 h-5 text-gray-600" />
                Pin/Unpin Features
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Pin icon on pinnable dashboards</li>
                <li>✓ Visual distinction for pinned tabs</li>
                <li>✓ Quick pin/unpin on click</li>
                <li>✓ Separate lists for pinned/unpinned</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Plus className="w-5 h-5 text-gray-600" />
                Dashboard Management
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Add new dashboard button</li>
                <li>✓ Delete dashboard option</li>
                <li>✓ Duplicate dashboard</li>
                <li>✓ Share dashboard</li>
                <li>✓ Archive dashboard group</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-gray-600" />
                Edit Features
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Inline rename editing</li>
                <li>✓ Edit dashboard configuration</li>
                <li>✓ Update position via API</li>
                <li>✓ SP People editing</li>
                <li>✓ Capacity Tracker editing</li>
              </ul>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TabControlTestPage;
